import { IconSprite } from "@platform/ui/components";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Amplify } from "aws-amplify";
import * as React from "react";
import { Helmet } from "react-helmet";
import { createBrowserRouter, Outlet, ScrollRestoration } from "react-router";
import { RouterProvider } from "react-router/dom";
import { queryClient } from "~/shared/api/core/query-client";
import { LoadingContainer } from "~/shared/components/ds/Spinner";
import { Toaster } from "~/shared/components/ds/Toast/Toast";
import { RootError } from "~/shared/components/errors/Error";
import Maintenance from "~/shared/components/Maintenance";
import { UserThemePreference } from "~/shared/components/UserThemePreference";
import { posthogClient } from "~/shared/lib/posthog/posthog-client";
import { PrivateRoutes } from "~/views/PrivateRoutes";
import { PublicRoutes } from "~/views/PublicRoutes";
import config from "./config";
import { persistOptions } from "./shared/api/core/query-persister";
import Symbols from "./shared/components/svg/Symbols";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.poolID,
      userPoolClientId: config.cognito.appID,
    },
  },
});

export function createAppRouter() {
  return createBrowserRouter([
    {
      element: <RootRoute />,
      children: [...PublicRoutes(queryClient, posthogClient), ...PrivateRoutes(queryClient, posthogClient)],
    },
  ]);
}

function RootRoute() {
  return (
    <div>
      <Outlet />
      <ScrollRestoration />
    </div>
  );
}

function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
  if (
    Number.parseInt(config.maintenanceMode ?? "", 10) === 1 &&
    window.location.search !== `?mcode=${config.maintenanceModeCode}`
  ) {
    return (
      <>
        <Helmet
          htmlAttributes={{ lang: "en" }}
          defaultTitle="BreakLine"
          titleTemplate="%s | BreakLine"
          meta={[{ charSet: "UTF-8" }, { name: "robots", content: "noindex" }]}
        />
        <Maintenance />
      </>
    );
  }
  return (
    <RootError>
      <React.Suspense fallback={<LoadingContainer level="full" />}>
        <Helmet
          htmlAttributes={{ lang: "en" }}
          defaultTitle="BreakLine"
          titleTemplate="%s | BreakLine"
          meta={[{ charSet: "UTF-8" }, { name: "robots", content: "noindex" }]}
        />
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          <UserThemePreference>
            <Symbols />
            <IconSprite />
            <RouterProvider router={router} />

            <Toaster />
            <ReactQueryDevtools position="left" initialIsOpen={false} />
          </UserThemePreference>
        </PersistQueryClientProvider>
      </React.Suspense>
    </RootError>
  );
}

export default App;
